import { type FormEvent, useState } from 'react';
import { useAddComment, useComments, useDeleteComment, useMe } from '../api/hooks';
import { useT } from '../i18n';
import { dts } from '../lib/format';
import { useUi } from '../state/ui';
import { Btn, Empty, H2, Surface, TextareaField } from './ui';

/**
 * Interní komentáře (kap. 44) k dokladu, partnerovi, zboží, kusu nebo platbě.
 * Jen pro členy firmy – na doklad, do e-mailu ani ven se nikdy nedostanou.
 */
export function Comments({
  companyId,
  entityType,
  entityId,
  title,
}: {
  companyId: string;
  entityType: 'document' | 'contact' | 'product' | 'unit' | 'payment';
  entityId: string;
  title?: string;
}) {
  const t = useT();
  const heading = title ?? t('comments.title');
  const { toast } = useUi();
  const { data: me } = useMe();
  const { data: comments = [], isPending } = useComments(entityType, entityId);
  const add = useAddComment(companyId, entityType, entityId);
  const remove = useDeleteComment(entityType, entityId);
  const [body, setBody] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    add.mutate(text, {
      onSuccess: () => setBody(''),
      onError: (err) => toast(err.message),
    });
  };

  return (
    <>
      <H2>{heading}</H2>
      <Surface>
        {!isPending && comments.length === 0 && (
          <Empty>Zatím bez komentáře. Co tu napíšeš, vidí jen kolegové.</Empty>
        )}
        {comments.map((c) => (
          <div className="row comment" key={c.id}>
            <div className="grow">
              <div className="l2">
                {c.author} · {dts(c.createdAt)}
                {c.editedAt ? ` · ${t('comments.edited')}` : ''}
              </div>
              <div className="l1 wrap">{c.body}</div>
            </div>
            {me?.id === c.userId && (
              <button
                type="button"
                className="link"
                aria-label="Smazat komentář"
                onClick={() => remove.mutate(c.id, { onError: (err) => toast(err.message) })}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <form onSubmit={submit} className="pad">
          <TextareaField
            id={`comment-${entityType}-${entityId}`}
            label="Nový komentář"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Např. zákazník platí vždy až po druhé upomínce…"
          />
          <Btn type="submit" sm loading={add.isPending} disabled={!body.trim()}>
            Přidat komentář
          </Btn>
        </form>
      </Surface>
    </>
  );
}
